/*
 * Cart cross-sell — adds the offered product.
 *
 * Visibility is Liquid's job: the box lives inside .js-contents, which cart.js
 * re-renders on every cart change, so a product that lands in the cart stops
 * being offered on its own. This file only handles the add.
 *
 * Two add paths. When an app block owns part of the cart footer, the add
 * navigates instead of patching the DOM: the pickup-point app answers an AJAX
 * cart write with a burst of its own /cart/update.js calls, and a removal that
 * lands during that burst is silently swallowed by Shopify. The rest of this
 * theme never writes to the cart over AJAX either (cart_type is 'page'), so
 * navigating keeps the upsell consistent with every other add-to-cart.
 */
class CartUpsellButton extends HTMLElement {
  constructor() {
    super();
    this.button = this.querySelector('button');
    this.errorElement = this.querySelector('.ljm-upsell__error');
    if (this.button) {
      this.button.addEventListener('click', this.onClick.bind(this));
    }
  }

  setLoading(isLoading) {
    if (!this.button) return;
    this.button.setAttribute('aria-disabled', isLoading ? 'true' : 'false');
    const spinner = this.button.querySelector('.ljm-upsell__spinner');
    if (spinner) spinner.hidden = !isLoading;
  }

  showError(message) {
    if (!this.errorElement) return;
    this.errorElement.textContent = message || '';
    this.errorElement.hidden = !message;
  }

  // Mirrors cart.js so the same sections stay in sync after an add.
  sectionsToRender() {
    const cartItems = document.querySelector('cart-items');
    if (cartItems && typeof cartItems.getSectionsToRender === 'function') {
      return cartItems.getSectionsToRender();
    }

    const sections = [];
    const itemsElement = document.getElementById('main-cart-items');
    const footerElement = document.getElementById('main-cart-footer');
    if (itemsElement) {
      sections.push({ id: 'main-cart-items', section: itemsElement.dataset.id, selector: '.js-contents' });
    }
    sections.push({ id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' });
    sections.push({ id: 'cart-live-region-text', section: 'cart-live-region-text', selector: '.shopify-section' });
    if (footerElement) {
      sections.push({ id: 'main-cart-footer', section: footerElement.dataset.id, selector: '.js-contents' });
    }
    return sections;
  }

  sectionInnerHTML(html, selector) {
    const parsed = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return parsed ? parsed.innerHTML : null;
  }

  onClick(event) {
    event.preventDefault();
    if (this.button.getAttribute('aria-disabled') === 'true') return;

    const variantId = Number(this.button.dataset.variantId);
    if (!variantId) return;

    this.showError('');
    this.setLoading(true);

    const navigateAfterAdd = !!document.querySelector('#main-cart-footer .shopify-app-block');
    const sections = navigateAfterAdd ? [] : this.sectionsToRender();

    const item = { id: variantId, quantity: 1 };
    const propertyName = this.button.dataset.propertyName;
    if (propertyName) item.properties = { [propertyName]: this.button.dataset.propertyValue || '' };
    const payload = { items: [item] };
    if (!navigateAfterAdd) {
      payload.sections = sections.map((section) => section.section);
      payload.sections_url = window.location.pathname;
    }

    const config = fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    config.body = JSON.stringify(payload);

    fetch(`${routes.cart_add_url}`, config)
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          this.showError(response.description || response.message || this.button.dataset.errorMessage);
          this.setLoading(false);
          return;
        }

        if (navigateAfterAdd) {
          // Leave the button in its loading state; the reload replaces the page.
          window.location.reload();
          return;
        }

        sections.forEach((section) => {
          const container = document.getElementById(section.id);
          if (!container || !response.sections || !response.sections[section.section]) return;
          const target = container.querySelector(section.selector) || container;
          const html = this.sectionInnerHTML(response.sections[section.section], section.selector);
          if (html !== null) target.innerHTML = html;
        });

        // Published as 'cart-items' because the sections above are already
        // repainted; cart.js would otherwise fetch and repaint them again.
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: response });
        }
      })
      .catch((error) => {
        console.error(error);
        this.showError(this.button.dataset.errorMessage);
        this.setLoading(false);
      });
  }
}

customElements.define('cart-upsell-button', CartUpsellButton);
